import React, { useState, useEffect } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';

import { SigningFontModal, Font } from './SigningFontModal';
import * as Store from '@apply/store';
import GoogleDocIFrameViewer from '../viewers/GoogleDocIFrameViewer';

export interface EsignNoticeModalProps {
    isOpen: boolean;
    onSignCallback?: (fontname: Font) => void;
    onCloseCallback?: () => void;
    signatureText?: string;
    notice?: Store.UserStore.Types.UserNotice;
}

export const EsignNoticeModal: React.FC<EsignNoticeModalProps> = ({
    isOpen,
    onSignCallback,
    onCloseCallback,
    signatureText,
    notice,
}) => {
    const [openSigningFontModal, setOpenSigningFontModal] = useState(false);
    const [agreeDisabled, setAgreeDisabled] = useState(true);
    const [signatureFont, setSignatureFont] = useState<Font>(undefined);
    const [signed, setSigned] = useState<boolean>(false);
    const [preSigned, set_preSigned] = useState<boolean>(false);

    useEffect(() => {
        if (notice?.eSignFont) {
            setSigned(true);
            set_preSigned(true);
            setSignatureFont({
                id: notice.eSignFont.id.toString(),
                name: notice.eSignFont.name,
            });
        } else {
            setSigned(false);
            set_preSigned(false);
            setSignatureFont(undefined);
        }
    }, [notice]);

    const handleSign = () => onSignCallback(signatureFont);
    const handleClose = () => {
        setSignatureFont(undefined);
        onCloseCallback();
    };

    const onClickToSign = (event) => {
        event.preventDefault();

        if (signatureFont) {
            handleSigningConfirm(signatureFont);
        } else {
            setOpenSigningFontModal(true);
        }
    };

    const handleSigningConfirm = (font) => {
        /* istanbul ignore else */
        if (font) setAgreeDisabled(false);

        setSigned(true);
        setSignatureFont(font);
        setOpenSigningFontModal(false);
    };

    const handleSigningCancel = () => {
        setOpenSigningFontModal(false);
    };

    const styles: Record<string, React.CSSProperties> = {
        flexboxContainer: {
            display: 'flex',
            justifyContent: 'center',
        },
        signatureContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        buttonDiv: {
            paddingTop: 20,
            paddingRight: 10,
            paddingBottom: 0,
            paddingLeft: 10,
        },
        button: {
            width: 175,
        },
        signButton: {
            margin: 0,
        },
        noPadding: {
            padding: 0,
        },
        iFrame: {
            border: '1px solid silver',
            width: '50vw',
            height: '50vh',
            margin: '.5vw',
        },
    };

    if (!notice) {
        return null;
    }

    return (
        <div>
            <Dialog
                open={isOpen}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                maxWidth='md'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            role='dialog'
                            style={{ width: '51vw' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>User Notice</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <GoogleDocIFrameViewer
                                    title='signNoticeIFrame'
                                    url={`${encodeURIComponent(notice.noticeFile.url)}`}
                                    height='700px'
                                    width='100%'
                                />
                                <div className='modal-content modal-content--esign'>
                                    <div className='modal-body'>
                                        <div
                                            className='standard-form'
                                            style={styles.noPadding}
                                        >
                                            {!preSigned && notice.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired && (
                                                <div style={styles.flexboxContainer}>
                                                    <div style={styles.signatureContainer}>
                                                        <div>
                                                            <label>
                                                                <strong>Signed by:</strong>
                                                            </label>
                                                        </div>
                                                        <div
                                                            style={{
                                                                marginTop: 10,
                                                                marginBottom: 10,
                                                            }}
                                                        >
                                                            {signed ? (
                                                                <div
                                                                    style={{
                                                                        fontSize: 24,
                                                                        fontFamily: signatureFont?.name,
                                                                    }}
                                                                >
                                                                    {signatureText}
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type='button'
                                                                    className='button hollow'
                                                                    style={styles.signButton}
                                                                    onClick={onClickToSign}
                                                                >
                                                                    Click to Sign
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label>
                                                                <strong>{signatureText}</strong>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className='modal-content-actions'>
                                                {!preSigned &&
                                                notice.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired ? (
                                                    <>
                                                        <button
                                                            type='button'
                                                            data-testid='accept-button'
                                                            className='button primary'
                                                            style={styles.button}
                                                            onClick={handleSign}
                                                            disabled={agreeDisabled}
                                                        >
                                                            Sign Notice
                                                        </button>
                                                        <button
                                                            type='button'
                                                            data-testid='do-not-accept-button'
                                                            className='button hollow'
                                                            style={styles.button}
                                                            onClick={handleClose}
                                                        >
                                                            Close
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type='button'
                                                        data-testid='do-not-accept-button'
                                                        className='button'
                                                        style={styles.button}
                                                        onClick={handleClose}
                                                    >
                                                        Close
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions></DialogActions>
            </Dialog>
            <SigningFontModal
                isOpen={openSigningFontModal}
                signatureText={signatureText}
                confirmCallback={handleSigningConfirm}
                cancelCallback={handleSigningCancel}
            />
        </div>
    );
};
